import { Injectable } from '@angular/core';
import { Task } from 'src/core/master-data/domain/task';
import { TaskRepository } from 'src/core/tasks/domain/task-repository';
import { TaskService } from 'src/core/tasks/domain/task.service';
import { StopTaskRequest } from './stop-task-request';
import { QueueService } from 'src/core/queue/domain/queue.service';
import { StopTaskQueueElement } from 'src/core/queue/domain/elements/stop-task-queue-element';
import { QueueElement, QueueElementError } from 'src/core/queue/domain/queue-element';
import { ConnectionException } from 'src/core/common/domain/exceptions/connection-exception';
import { UnauthorizedException } from 'src/core/authentication/domain/exceptions/unauthorized-exception';

@Injectable({
  providedIn: 'any',
})
export class StopTask {
  private task: Task;
  private isSavedToServer = false;
  private isStoppedToServer = false;
  private isRetry = false;

  constructor(
    private readonly taskRepository: TaskRepository,
    private readonly taskService: TaskService,
    private readonly queueService: QueueService
  ) {}

  async execute(request: StopTaskRequest): Promise<void> {
    this.task = request.task;
    const queueElement = request.queueElement;
    this.isRetry = queueElement !== null;
    this.isSavedToServer = queueElement !== null ? queueElement.operationStatus.isSavedToServer : false;
    this.isStoppedToServer = queueElement !== null ? queueElement.operationStatus.isStoppedToServer : false;

    try {
      if (!this.isRetry) {
        this.task.stop();
        await this.saveTaskIntoLocalDevice();
      }

      const taskIsInQueue = this.queueService.hasQueueElementWithTaskId(this.task.id);
      if (taskIsInQueue && !this.isRetry) {
        await this.enqueueOperation();
        return;
      }

      await this.saveTaskToExternalServer(this.task);
      await this.stopTaskToExternalServer(this.task);
    } catch (exception) {
      if (exception instanceof ConnectionException) {
        if (this.isRetry) {
          await this.keepQueueElementForNextRetry(queueElement);
          throw exception;
        }

        await this.enqueueOperation();
        return;
      }

      // exceptions during retrying do not enqueue or rollback the task, but store error to display in list
      if (this.isRetry) {
        await this.storeErrorsInQueueElement(exception, queueElement);
        throw exception; // propagate exception without doing anything else
      }

      // otherwise, rollback
      this.task.rollback();
      await this.saveTaskIntoLocalDevice();
      throw exception; // propagate exception
    }
  }

  private async saveTaskIntoLocalDevice() {
    await this.taskService.saveTask(this.task);
  }

  private async saveTaskToExternalServer(task: Task) {
    if (this.isSavedToServer) {
      return;
    }
    await this.taskRepository.save(task);
    this.isSavedToServer = true;
  }

  private async stopTaskToExternalServer(task: Task) {
    if (this.isStoppedToServer) {
      return;
    }
    await this.taskRepository.stop(task);
    this.isStoppedToServer = true;
  }

  private async enqueueOperation(errors: Array<QueueElementError> = []) {
    const operationStatus = {
      isSavedToServer: this.isSavedToServer,
      isStoppedToServer: this.isStoppedToServer,
    };
    await this.queueService.enqueue(StopTaskQueueElement.fromTask(this.task, operationStatus, errors));
  }

  /**
   * A retried element is still in the queue, and returning normally makes the caller drop it as processed,
   * so enqueueing a new one would move the operation behind later ones. Throwing keeps the original in
   * place, with the progress made so far, so the next retry does not repeat what already reached the server.
   */
  private async keepQueueElementForNextRetry(queueElement: StopTaskQueueElement) {
    await this.updateQueueElementWithErrors(queueElement, queueElement.errors);
  }

  private async storeErrorsInQueueElement(exception: unknown, queueElement: StopTaskQueueElement) {
    // an expired session is handled by the session flow, it says nothing about this operation
    if (exception instanceof UnauthorizedException) {
      return;
    }
    await this.updateQueueElementWithErrors(queueElement, QueueElement.errorsFromException(exception));
  }

  private async updateQueueElementWithErrors(queueElement: StopTaskQueueElement, errors: Array<QueueElementError>) {
    const operationStatus = {
      isSavedToServer: this.isSavedToServer,
      isStoppedToServer: this.isStoppedToServer,
    };
    queueElement.update(this.task, operationStatus, errors);
    await this.queueService.updateQueueElement(queueElement);
  }
}
